/**
 * breadcrumbTrail.js
 * Construye la ruta de navegación (breadcrumbs) a partir de la URL.
 *
 * Se apoya en `menuConfig.js` para no duplicar etiquetas: casi todas las
 * rutas de la aplicación son entradas del menú lateral, así que la jerarquía
 * sale de ahí (grupo › entrada) y solo hay casos especiales para rutas de
 * detalle que no están en el menú.
 */

import { DOMAIN_GROUPS, STANDALONE_ITEMS } from '../menuConfig';

/** Rutas sueltas con su propia miga (no están en el menú lateral). */
const EXTRA_ROUTES = {
  '/audit': [{ label: 'Registros' }, { label: 'Auditoría' }],
  '/settings': [{ label: 'Ajustes' }],
  '/perfil': [{ label: 'Mi perfil' }],
  '/admin/platform': [{ label: 'Administración' }, { label: 'Plataforma CMDB' }],
  '/systems': [{ label: 'Sistemas y Virtualización' }],
  '/applications': [{ label: 'Aplicaciones y Software' }],
  '/security': [{ label: 'Seguridad' }],
  '/documentation': [{ label: 'Documentación' }],
  '/documentation/new': [{ label: 'Documentación', to: '/documentation' }, { label: 'Nuevo documento' }],
};

/** Traducción de los primeros segmentos de URL usados en rutas sueltas. */
const SEGMENT_LABELS = {
  infrastructure: 'Infraestructura',
  networks: 'Redes',
  virt: 'Virtualización',
  wifi: 'WiFi',
  wan: 'WAN',
  services: 'Servicios',
  apps: 'Aplicaciones',
  admin: 'Administración',
  backups: 'Backups',
  docs: 'Documentación',
  documentation: 'Documentación',
};

/** Migas extra para páginas de detalle que no son entradas de menú. */
const DETAIL_RULES = [
  { pattern: /^\/infrastructure\/rack\/([^/]+)$/, label: (id) => `Rack #${id}` },
  { pattern: /^\/rack\/([^/]+)$/, label: (id) => `Rack #${id}` },
  { pattern: /^\/infrastructure\/racks\/([^/]+)$/, label: (id) => `Ubicación #${id}` },
  { pattern: /^\/racks\/([^/]+)$/, label: (id) => `Ubicación #${id}` },
  // `/documentation/new` ya tiene su miga propia: no debe caer en «Documento».
  { pattern: /^\/documentation\/([^/]+)$/, label: (id) => (id === 'new' ? null : 'Documento') },
  { pattern: /^\/docs\/([^/]+)\/([^/]+)$/, label: () => 'Documentos del elemento' },
  // La tabla de equipos cuelga del dashboard de Equipos (misma entrada de menú).
  { pattern: /^\/infrastructure\/workstations\/list$/, label: () => 'Lista de equipos' },
];

/**
 * Prefijos que en el menú cuelgan de otra ruta (p. ej. el visor de un rack
 * vive en `/infrastructure/rack/:id` y no bajo `/infrastructure/racks/:id`).
 */
const MENU_PATH_ALIASES = [
  { prefix: '/infrastructure/rack/', menuPath: '/infrastructure/racks' },
  { prefix: '/rack/', menuPath: '/infrastructure/racks' },
];

function resolveMenuPath(pathname) {
  const alias = MENU_PATH_ALIASES.find(({ prefix }) => pathname.startsWith(prefix));
  return alias ? alias.menuPath : pathname;
}

function capitalize(value) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ');
}

/**
 * Busca la entrada de menú cuyo `path` es igual al de la URL o un prefijo de
 * ella (gana la coincidencia más larga, p. ej. `/services/designer` antes que
 * `/services`).
 */
function findMenuItemByPath(pathname) {
  let best = null;

  const consider = (group, item) => {
    if (!item?.path) return;
    const isExact = pathname === item.path;
    const isChild = pathname.startsWith(`${item.path}/`);
    if (!isExact && !isChild) return;
    if (!best || item.path.length > best.item.path.length) {
      best = { group, item };
    }
  };

  DOMAIN_GROUPS.forEach((group) => group.items.forEach((item) => consider(group, item)));
  STANDALONE_ITEMS.forEach((item) => consider(null, item));

  return best;
}

function findDetailCrumb(pathname) {
  for (const rule of DETAIL_RULES) {
    const match = pathname.match(rule.pattern);
    if (match) {
      const label = rule.label(...match.slice(1));
      if (label) return label;
    }
  }
  return null;
}

/**
 * @param {string} pathname  Ruta actual (`useLocation().pathname`).
 * @param {string|null} detailLabel  Etiqueta del registro abierto en un
 *   formulario/overlay (se registra con `<DetailCrumb />`).
 * @returns {{label: string, to?: string|null}[]} Migas de pan, de raíz a actual.
 */
export function buildBreadcrumbTrail(pathname, detailLabel = null, t = null) {
  const path = pathname || '/';
  const tr = (key, fallback) => {
    if (!t) return fallback;
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  // Dashboard es la raíz lógica de la aplicación.
  if (path === '/' || path === '/dashboard') {
    return [{ label: tr('nav.home', 'Inicio'), to: null }];
  }

  const trail = [{ label: tr('nav.home', 'Inicio'), to: '/dashboard' }];
  const menuMatch = findMenuItemByPath(resolveMenuPath(path));

  if (menuMatch) {
    if (menuMatch.group) {
      trail.push({
        label: tr(`menu.groups.${menuMatch.group.key}`, menuMatch.group.label),
        to: null,
      });
    }
    trail.push({
      label: tr(`menu.items.${menuMatch.item.key}`, menuMatch.item.label),
      to: menuMatch.item.path,
    });
  } else if (EXTRA_ROUTES[path]) {
    EXTRA_ROUTES[path].forEach((crumb) => trail.push({ ...crumb, to: crumb.to || null }));
  } else if (path.startsWith('/documentation/') || path.startsWith('/docs/')) {
    trail.push({ label: 'Documentación', to: '/documentation' });
  } else {
    const firstSegment = path.split('/').filter(Boolean)[0] || '';
    trail.push({
      label: SEGMENT_LABELS[firstSegment] || capitalize(firstSegment),
      to: null,
    });
  }

  const detailCrumb = findDetailCrumb(path);
  if (detailCrumb) trail.push({ label: detailCrumb, to: null });

  if (detailLabel) trail.push({ label: detailLabel, to: null });

  // La última miga siempre es la página actual: no debe ser clicable.
  return trail.map((crumb, index) =>
    index === trail.length - 1 ? { ...crumb, to: null } : crumb
  );
}

export default buildBreadcrumbTrail;
